/* eslint-disable react-hooks/exhaustive-deps */
import './index.less';

import {
  Button,
  Flex,
  Icon,
  Link,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Scrollbar,
  Space,
  Text,
  useColorMode,
  useColorStyle,
  Alert,
} from '@tonic-one/react';
import { CheckCircleOIcon, FileCopyOIcon } from '@tonic-one/react-icons';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { useToggle } from '@tonic-one/react-hooks';
import CSpinner from '../../../components/CSpinner';
import { useEffect, useRef, useState } from 'react';
import { Steps } from 'antd';
import {
  DataSyncFailed,
  DataSynced,
  DataSyncing,
  GmailNote,
  GoogleLoginSvg,
  ProvisionOrgInfo,
  StepIcon,
  UserIcon,
} from './share';
import { Trans, useTranslation } from 'react-i18next';
import { deleteCookie, getProvisionCookie } from '../../../utils/common';
import { ProvisionApi } from '@Apis';
import {
  AuthenticationInfoRes,
  EnabledPolicyTypeEnum,
  GmailInlineSetting,
  IdSyncStatus,
  ProvisionModalState,
  ProvisionStatusEnum,
  RejectProvisionReason,
  TokenStatus,
} from '../../../types/provision';
import { reportPendo } from '../../../utils/setupPendo';
import ErrorAlert from '../../../components/Common/ErrorAlert';
import {
  CAS_DOC_LINK,
  getCasOLHLink,
  THIRD_PARTY_LINK,
} from '../../../utils/casDocLink';
import i18next from 'i18next';
import RejectProvisionModal from './rejectProvisionModal';
import { App, Service } from '@Types/app';

enum GmailInlineStatus {
  NotProvision = 0,
  Provisioning = 1,
  Provisioned = 2,
}

const GmailInlineModal = ({
  orgId,
  onClose,
  isOpen,
  provisionStatus,
}: ProvisionModalState) => {
  const { t } = useTranslation(['provision', 'common']);
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const StatusMap = {
    [ProvisionStatusEnum.NotStarted]: GmailInlineStatus.NotProvision,
    [ProvisionStatusEnum.Provisioning]: GmailInlineStatus.Provisioning,
    [ProvisionStatusEnum.Provisioned]: GmailInlineStatus.Provisioned,
  };

  const [syncDataFailFlag, setSyncDataFailFlag] = useState(
    provisionStatus.status === ProvisionStatusEnum.Failure &&
      [IdSyncStatus.Failure, 'FAILURE'].includes(
        provisionStatus?.idSync?.status
      )
  );
  const [gmailInlineStatus, setGmailInlineStatus] = useState<GmailInlineStatus>(
    () => {
      if (Object.keys(StatusMap).includes(provisionStatus.status)) {
        return StatusMap[provisionStatus.status as keyof typeof StatusMap];
      } else {
        //provisionStatus.status = failure
        //token failure
        if (provisionStatus.authorization.status === TokenStatus.Failure) {
          return GmailInlineStatus.NotProvision;
        } else {
          //id sync fail
          return GmailInlineStatus.Provisioning;
          //TODO: 同时设置 id sync 为失败的情况
        }
      }
    }
  );
  const [authenticationInfo, setAuthenticationInfo] =
    useState<null | AuthenticationInfoRes>(null);
  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined);

  const [loading, toggleLoading] = useToggle(false);
  const timeRef = useRef<any>(null);
  const [rejectReason, setRejectReason] =
    useState<RejectProvisionReason | null>(null);

  const onClick = () => {
    const authUrl = authenticationInfo?.[0]?.authenticationUrl;
    if (!authUrl) {
      return;
    }
    setErrorMsg(undefined);
    reportPendo('Provision.GrantPermission.Click', {
      orgId,
      service: 'gmailInline',
    });
    toggleLoading(true);
    window.open(authUrl, '_blank');
    if (timeRef.current) {
      clearInterval(timeRef.current);
    }
    timeRef.current = setInterval(async () => {
      //{"code":"","state":"","serviceType":"22","status":"success"}
      //{"code":"","state":"","serviceType":"22","status":"failure","error":"780"}
      const gmailInlineProvisionInfo = getProvisionCookie(
        'provision_status_22'
      );
      if (gmailInlineProvisionInfo) {
        clearInterval(timeRef.current);
        deleteCookie('provision_status_');
        const { error, code } = JSON.parse(gmailInlineProvisionInfo);
        if (error) {
          setErrorMsg(t(`provision.service.gmailInline.error.deniedGrant`));
        } else {
          try {
            const res = await ProvisionApi.apiSaveProvisionInfo(
              App.GmailInline,
              code,
              EnabledPolicyTypeEnum.None,
              orgId
            );
            if (res.data?.status === 'failure') {
              setErrorMsg(
                t([
                  `provision.service.gmail.error.${res.data?.reason}`,
                  'common.apiException.internalError',
                ])
              );
            } else {
              setGmailInlineStatus(GmailInlineStatus.Provisioning);
            }
          } catch (e) {
            setErrorMsg(t('common.apiException.internalError'));
            console.error(JSON.stringify(e));
          }
        }
        toggleLoading(false);
      }
    }, 1000);
  };

  useEffect(() => {
    if (timeRef.current) {
      clearInterval(timeRef.current);
    }
    return () => {
      if (timeRef.current) {
        clearInterval(timeRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (gmailInlineStatus === GmailInlineStatus.NotProvision) {
      ProvisionApi.apiGetAuthenticationInfo(Service.GmailInline, orgId)
        .then((res) => {
          setAuthenticationInfo(res.data);
        })
        .catch((e) => {
          console.error(JSON.stringify(e));
          const errorCode = e.data?.error?.details?.[0].errorCode;
          if (errorCode === RejectProvisionReason.ServiceConflict) {
            setRejectReason(errorCode);
          }
        });
    }
  }, []);

  if (rejectReason) {
    return (
      <RejectProvisionModal
        onClose={onClose}
        service={Service.GmailInline}
        reason={rejectReason}
      />
    );
  }

  if (
    gmailInlineStatus === GmailInlineStatus.NotProvision &&
    !authenticationInfo
  ) {
    return <></>;
  }

  return (
    <>
      {loading && <CSpinner />}
      <Modal isOpen={isOpen} isClosable onClose={onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          {/* TODO i18n: "Grant Access to Gmail" */}
          <ModalHeader>{t('provision.service.gmailInline.title')}</ModalHeader>
          <Scrollbar overflow="scroll" maxHeight="60vh">
            <ModalBody mt="0px !important">
              <ProvisionOrgInfo orgId={orgId} />
              <GmailInlineSpecialScenarioAlert />
              {errorMsg && (
                <ErrorAlert
                  errorMsg={errorMsg}
                  onClose={() => setErrorMsg(undefined)}
                />
              )}
              <Flex direction="column" gap="16px">
                <Steps
                  direction="vertical"
                  current={gmailInlineStatus}
                  items={[
                    {
                      title: (
                        <Text className={`stepText ${colorMode}`}>
                          {t('provision.service.gmailInline.installApp')}{' '}
                          <Link
                            onClick={() => {
                              window.open(
                                authenticationInfo?.[0]?.installAppUrl || '',
                                '_blank'
                              );
                            }}
                          >
                            {t('common.action.label.clickHere')}
                          </Link>
                        </Text>
                      ),
                      description: <Space height="16px" />,
                      icon: <UserIcon />,
                    },
                    {
                      title: (
                        <Text className={`stepText ${colorMode}`}>
                          {t('provision.service.gmail.grantPermission')}
                        </Text>
                      ),
                      description:
                        gmailInlineStatus < GmailInlineStatus.Provisioning ? (
                          <Button
                            px="0px"
                            borderWidth="0px"
                            variant="ghost"
                            disabled={
                              !(
                                gmailInlineStatus ===
                                GmailInlineStatus.NotProvision
                              )
                            }
                            onClick={() => {
                              onClick();
                            }}
                          >
                            <GoogleLoginSvg
                              disabled={
                                !(
                                  gmailInlineStatus ===
                                  GmailInlineStatus.NotProvision
                                )
                              }
                            />
                          </Button>
                        ) : (
                          <Text color={colorStyle.color.tertiary}>
                            {t('provision.common.permissionGranted')}
                          </Text>
                        ),
                      icon: (
                        <StepIcon
                          checked={
                            gmailInlineStatus >= GmailInlineStatus.Provisioning
                          }
                        />
                      ),
                    },
                    {
                      title: (
                        <Text className={`stepText ${colorMode}`}>
                          <Trans
                            i18nKey="provision.service.gmailInline.userConfiguration"
                            ns="provision"
                          >
                            placeholder
                            <Link
                              href={getCasOLHLink(
                                CAS_DOC_LINK.provision.gmailInline.instruction,
                                i18next.language
                              )}
                              target="_blank"
                            >
                              placeholder
                            </Link>
                            placeholder
                          </Trans>
                        </Text>
                      ),
                      description: <UserConfiguration orgId={orgId} />,
                      icon: <UserIcon />,
                    },
                  ]}
                />
                {gmailInlineStatus === GmailInlineStatus.Provisioning &&
                  (syncDataFailFlag ? (
                    <DataSyncFailed
                      service={Service.GmailInline}
                      onRetry={async () => {
                        await ProvisionApi.apiRetrySyncData(
                          Service.GmailInline,
                          orgId
                        );
                        setGmailInlineStatus(GmailInlineStatus.Provisioning);
                        setSyncDataFailFlag(false);
                      }}
                    />
                  ) : (
                    <DataSyncing
                      onRefresh={async () => {
                        const res = await ProvisionApi.apiGetProvisionStatus(
                          Service.GmailInline,
                          orgId
                        );
                        if (
                          res.data.status === ProvisionStatusEnum.Provisioned
                        ) {
                          setGmailInlineStatus(GmailInlineStatus.Provisioned);
                        }
                      }}
                    />
                  ))}
                {gmailInlineStatus === GmailInlineStatus.Provisioned && (
                  <DataSynced />
                )}
              </Flex>
              <GmailNote />
            </ModalBody>
          </Scrollbar>
          <ModalFooter>
            <Button minWidth="20x" onClick={onClose}>
              {t('common.action.label.close')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default GmailInlineModal;

const UserConfiguration = ({ orgId }: { orgId: string }) => {
  const { t } = useTranslation('provision');
  const [colorMode] = useColorMode();
  const [copyIndex, setCopyIndex] = useState<0 | 1 | 2 | 3 | 4>(0);
  const [gmailInlineSetting, setGmailInlineSetting] =
    useState<GmailInlineSetting | null>(null);
  useEffect(() => {
    ProvisionApi.apiGetGmailInlineSetting(orgId).then((res) => {
      setGmailInlineSetting(res.data);
    });
  }, []);
  return (
    <Flex direction="column" className={`stepText ${colorMode}`} gap="8px">
      <Text fontSize="14px" lineHeight="20px" fontWeight="400" pt="8px">
        {t('provision.service.gmailInline.forInbound')}:
      </Text>
      <CasHostnameCopyToClipboard
        text={gmailInlineSetting?.inboundHostname || ''}
        onCopy={() => {
          setCopyIndex(1);
        }}
        copied={copyIndex === 1}
      />
      <LoopHeaderCopyToClipboard
        text={gmailInlineSetting?.inboundLoopHeader || ''}
        onCopy={() => {
          setCopyIndex(2);
        }}
        copied={copyIndex === 2}
      />
      <Text fontSize="14px" lineHeight="20px" fontWeight="400" pt="8px">
        {t('provision.service.gmailInline.forOutbound')}:
      </Text>
      <CasHostnameCopyToClipboard
        text={gmailInlineSetting?.outboundHostname || ''}
        onCopy={() => {
          setCopyIndex(3);
        }}
        copied={copyIndex === 3}
      />
      <LoopHeaderCopyToClipboard
        text={gmailInlineSetting?.outboundLoopHeader || ''}
        onCopy={() => {
          setCopyIndex(4);
        }}
        copied={copyIndex === 4}
      />
    </Flex>
  );
};

const CasHostnameCopyToClipboard = ({
  text,
  onCopy,
  copied,
}: {
  text: string;
  onCopy: () => void;
  copied: boolean;
}) => {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { t } = useTranslation('provision');
  return (
    <Flex align="center" color={colorStyle.color.secondary}>
      <Text
        fontSize="14px"
        lineHeight="18px"
        fontWeight="400"
        wordBreak="break-all"
      >
        {t('provision.service.gmailInline.casHostname', {
          hostname: text,
        })}
      </Text>
      <CopyToClipboard text={text} onCopy={onCopy}>
        <Icon as={FileCopyOIcon} cursor="pointer" />
      </CopyToClipboard>
      {copied ? (
        <Icon as={CheckCircleOIcon} color={colorStyle.color.success} />
      ) : (
        <Space minWidth="16px" />
      )}
    </Flex>
  );
};

const LoopHeaderCopyToClipboard = ({
  text,
  onCopy,
  copied,
}: {
  text: string;
  onCopy: () => void;
  copied: boolean;
}) => {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { t } = useTranslation('provision');
  return (
    <Flex align="center" color={colorStyle.color.secondary}>
      <Text
        fontSize="14px"
        lineHeight="18px"
        fontWeight="400"
        wordBreak="break-all"
      >
        {t('provision.service.gmailInline.loopPreventionHeader', {
          header: text,
        })}
      </Text>
      <CopyToClipboard text={text} onCopy={onCopy}>
        <Icon as={FileCopyOIcon} cursor="pointer" />
      </CopyToClipboard>
      {copied ? (
        <Icon as={CheckCircleOIcon} color={colorStyle.color.success} />
      ) : (
        <Space minWidth="16px" />
      )}
    </Flex>
  );
};

const GmailInlineSpecialScenarioAlert = () => {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  return (
    <Alert border="0px" severity="info" variant="outline" px="0" pt="0">
      <Text
        fontSize="14px"
        lineHeight="20px"
        fontWeight="400"
        color={colorStyle.color.secondary}
      >
        <Trans
          i18nKey="provision.service.gmailInline.scenarioTips"
          ns="provision"
        >
          placeholder
          <Link
            href={THIRD_PARTY_LINK.gmailComprehensiveMailStorage}
            target="_blank"
          >
            placeholder
          </Link>
          placeholder
          <Link
            href={getCasOLHLink(
              CAS_DOC_LINK.onlineHelpMenu.faq,
              i18next.language
            )}
            target="_blank"
          >
            placeholder
          </Link>
        </Trans>
      </Text>
    </Alert>
  );
};
